import { Capacitor } from "@capacitor/core";
import { isAndroid, isIOS } from "react-device-detect";
import {
  CUSTOMER_APP_STORES,
  CUSTOMER_MARKETPLACE_URL,
  type CustomerAppStore,
} from "../../../../../shared/constants/customerAppStores";

export { CUSTOMER_APP_STORES, CUSTOMER_MARKETPLACE_URL };

export const REGISTER_ROLES = ["business", "customer"] as const;
export type RegisterRole = (typeof REGISTER_ROLES)[number];

export type DevicePlatform = "ios" | "android" | "other";

export function detectDevicePlatform(): DevicePlatform {
  // Inside the Capacitor shell the platform is known for certain; the user
  // agent is only the fallback for mobile browsers.
  const nativePlatform = Capacitor.getPlatform();

  if (nativePlatform === "ios" || nativePlatform === "android") {
    return nativePlatform;
  }

  if (isIOS) return "ios";
  if (isAndroid) return "android";

  return "other";
}

/** The stores worth offering: the matching one on a phone, both elsewhere. */
export function getCustomerAppStores(
  platform: DevicePlatform,
): CustomerAppStore[] {
  if (platform === "ios") return ["appStore"];
  if (platform === "android") return ["googlePlay"];

  return ["appStore", "googlePlay"];
}

/** A store to send the visitor to straight away, or null when it is ambiguous. */
export function getDirectStoreUrl(platform: DevicePlatform): string | null {
  const stores = getCustomerAppStores(platform);
  const [onlyStore] = stores;

  if (stores.length !== 1 || !onlyStore) return null;

  return CUSTOMER_APP_STORES[onlyStore];
}

/** The code a sign-up link carries: `referralCode`, or the older `code`. */
export function pickLinkCode(
  referralCode: string | null,
  code: string | null,
): string | null {
  const value = (referralCode ?? code)?.trim();

  return value ? value : null;
}

/** Only providers have codes, so a link with one skips the role question. */
export function resolveRegisterRole(
  role: RegisterRole | null,
  linkCode: string | null,
): RegisterRole | null {
  return role ?? (linkCode ? "business" : null);
}
